import { PROPERTY_EXTRA_FIELDS } from "models/NotificationSettings";
import {
  ACTION_APPROVE,
  ACTION_CREATE,
  ACTION_DELETE,
  ACTION_DEMOTE,
  ACTION_DETAILS,
  ACTION_GLOBAL_FILTER,
  ACTION_LIST,
  ACTION_PROMOTE,
  ACTION_TEST,
  ACTION_UPDATE,
  ACTIONS_CRU,
  ACTIONS_READ,
  ACTIONS_WRITE,
} from "models/role/action";
import {
  PROPERTY_ID,
  SUBJECT_ACCOUNT,
  SUBJECT_ADMIN,
  SUBJECT_BANK,
  SUBJECT_CASH_MANAGEMENT,
  SUBJECT_CASH_MANAGEMENT_ACCOUNT,
  SUBJECT_CASH_MANAGEMENT_PROVIDER_SETUP,
  SUBJECT_CASH_MANAGEMENT_SETTLEMENT,
  SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION,
  SUBJECT_CASH_MANAGEMENT_VIRTUAL_ACCOUNTS,
  SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNT_TRANSACTIONS,
  SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
  SUBJECT_CHECKOUT,
  SUBJECT_CIRCUIT_BREAKER,
  SUBJECT_CLIENT,
  SUBJECT_CLIENT_VERIFY_NOTIFICATIONS,
  SUBJECT_CLOSED_LOOP_PAYOUTS,
  SUBJECT_CREDIT_NOTIFICATION_PREFERENCES,
  SUBJECT_CUSTOMER,
  SUBJECT_CUSTOMER_BANK_LIST,
  SUBJECT_CUSTOMER_COUNTRIES,
  SUBJECT_CUSTOMER_PAYMENTS_PROVIDERS,
  SUBJECT_CUSTOMER_PRODUCT_FLAGS,
  SUBJECT_CUSTOMER_PUBLIC_KEYS,
  SUBJECT_DYNAMIC_BENEFICIARY,
  SUBJECT_NOTIFICATION_SETTINGS,
  SUBJECT_OPEN_LOOP_PAYOUTS,
  SUBJECT_OWNER_USER,
  SUBJECT_PAY_BY_LINK,
  SUBJECT_PAYMENT,
  SUBJECT_PAYMENT_EXTERNAL_LINKS,
  SUBJECT_PAYMENT_NOTIFICATION,
  SUBJECT_PAYOUTS_NOTIFICATIONS,
  SUBJECT_REFUND,
  SUBJECT_USER,
  SUBJECT_VX2_ADDRESS,
  SUBJECT_VX2_ONBOARDING,
  SUBJECT_VX2_PAYMENT,
  SUBJECT_VX2_REFUND,
  SUBJECT_VX2_SETTLEMENT,
  SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
  SUBJECT_VX2_WALLET,
  SUBJECT_WORKFLOWS,
} from "models/role/subject";
import {
  PROPERTY_ACTIVE as CLIENT_PROPERTY_ACTIVE,
  PROPERTY_REDIRECT_SIGNATURE,
  PROPERTY_SECRET,
} from "modules/applications/models/Client";
import {
  PROPERTY_PRODUCTION_ACCOUNT_BALANCE,
  PROPERTY_SANDBOX_ACCOUNT_BALANCE,
} from "modules/connect/models/CashManagementAccount";
import {
  PROPERTY_PAYOUT_NOTIFICATIONS_SECRET,
  PROPERTY_PAYOUT_NOTIFICATIONS_STATUS,
} from "modules/connect/models/Notifications";
import {
  PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
  PROPERTY_PRODUCTION_SETTLEMENT_BATCHES,
  PROPERTY_PRODUCTION_SETTLEMENT_CONFIGURATION,
  PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
  PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
  PROPERTY_SANDBOX_SETTLEMENT_BATCHES,
  PROPERTY_SANDBOX_SETTLEMENT_CONFIGURATION,
  PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
} from "modules/connect/models/Settlements";
import {
  PROPERTY_ACTIVE as CUSTOMER_PROPERTY_ACTIVE,
  PROPERTY_ADDRESS,
  PROPERTY_CIRCUIT_BREAKER_ENABLED,
  PROPERTY_CREDENTIALS,
  PROPERTY_MERCHANT_CATEGORY_CODE,
  PROPERTY_PAYMENT_PREFERENCES,
  PROPERTY_PRODUCTION_ACCOUNTS,
  PROPERTY_PRODUCTION_CONNECT,
  PROPERTY_PRODUCTION_GATEWAY,
  PROPERTY_PRODUCTION_GATEWAY_AUSTRALIA,
  PROPERTY_PRODUCTION_GATEWAY_US,
  PROPERTY_PRODUCTION_INTERNAL_FX,
  PROPERTY_PRODUCTION_MANDATE_MANAGEMENT,
  PROPERTY_PRODUCTION_MATCHMETER,
  PROPERTY_PRODUCTION_NAMED_ACCOUNTS,
  PROPERTY_PRODUCTION_OPEN_LOOP_PAYOUTS,
  PROPERTY_PRODUCTION_PAY_BY_LINK,
  PROPERTY_PRODUCTION_PAYOUTS,
  PROPERTY_PRODUCTION_REFUND,
  PROPERTY_PRODUCTION_SAME_DAY_ACH,
  PROPERTY_PRODUCTION_SIGNAL,
  PROPERTY_PRODUCTION_TRANSFORMER,
  PROPERTY_PRODUCTION_VERIFY,
  PROPERTY_PRODUCTION_VX2,
  PROPERTY_SANDBOX_ACCOUNTS,
  PROPERTY_SANDBOX_CONNECT,
  PROPERTY_SANDBOX_GATEWAY,
  PROPERTY_SANDBOX_GATEWAY_AUSTRALIA,
  PROPERTY_SANDBOX_GATEWAY_US,
  PROPERTY_SANDBOX_INTERNAL_FX,
  PROPERTY_SANDBOX_MANDATE_MANAGEMENT,
  PROPERTY_SANDBOX_MATCHMETER,
  PROPERTY_SANDBOX_NAMED_ACCOUNTS,
  PROPERTY_SANDBOX_OPEN_LOOP_PAYOUTS,
  PROPERTY_SANDBOX_PAY_BY_LINK,
  PROPERTY_SANDBOX_PAYOUTS,
  PROPERTY_SANDBOX_REFUND,
  PROPERTY_SANDBOX_SAME_DAY_ACH,
  PROPERTY_SANDBOX_SIGNAL,
  PROPERTY_SANDBOX_TRANSFORMER,
  PROPERTY_SANDBOX_VERIFY,
  PROPERTY_SANDBOX_VX2,
  PROPERTY_TERMINATE_AT,
  PROPERTY_TYPE,
} from "modules/customers/models/Customer";
import {
  PROPERTY_BANK_RESPONSES,
  PROPERTY_BENEFICIARY_ACCOUNT_IDENTIFICATIONS,
  PROPERTY_PROVIDER,
  PROPERTY_SENDER,
  PROPERTY_SENDER_ACCOUNT_NAME,
  PROPERTY_SENDER_CHECKOUT,
  PROPERTY_SENDER_PAYMENT_PROVIDER,
  PROPERTY_SENDER_VOLT_CONNECT,
} from "modules/payments/models/Payment";
import { USER_TYPE } from "modules/user-management/constants";
import { PROPERTY_ACTIVE as USER_PROPERTY_ACTIVE } from "modules/user-management/models/User";

import type { PermissionsAll } from "modules/auth/constants/permissions";
import type { UserType } from "modules/user-management/constants";
import type { UUID } from "utils";

import type { AbilityInterface } from "./ability";

type permissionApplyFunction = (args: {
  ability: AbilityInterface;
  voltEmployee: boolean;
  customerId: UUID | null;
  hierarchyIds: UUID[];
  userId: UUID;
  userType: UserType;
}) => void;

export const applyPermissions: Record<PermissionsAll, permissionApplyFunction> =
  {
    // payments
    PAYMENTS_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTION_LIST, SUBJECT_PAYMENT);
      ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, PROPERTY_ID);
      ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, PROPERTY_SENDER);

      ability.can(ACTIONS_READ, SUBJECT_BANK, [PROPERTY_ID]);

      ability.can(
        ACTION_DETAILS,
        SUBJECT_PAYMENT,
        PROPERTY_BENEFICIARY_ACCOUNT_IDENTIFICATIONS
      );

      ability.can(ACTION_DETAILS, SUBJECT_REFUND);
      ability.can(ACTION_LIST, SUBJECT_REFUND);

      ability.can(ACTIONS_READ, SUBJECT_CLOSED_LOOP_PAYOUTS);
      ability.can(ACTIONS_READ, SUBJECT_OPEN_LOOP_PAYOUTS);

      ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_SETTLEMENT);
      ability.can(ACTION_LIST, SUBJECT_CASH_MANAGEMENT_SETTLEMENT);

      ability.can(
        ACTION_DETAILS,
        SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNT_TRANSACTIONS
      );
      ability.can(
        ACTION_LIST,
        SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNT_TRANSACTIONS
      );

      // Stablecoin: VIEW_PAYMENTS, VIEW_REFUNDS
      ability.can(ACTIONS_READ, SUBJECT_VX2_PAYMENT);
      ability.can(ACTIONS_READ, SUBJECT_VX2_REFUND);

      if (voltEmployee) {
        ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, [
          PROPERTY_BANK_RESPONSES,
          PROPERTY_PROVIDER,
        ]);
        ability.can(ACTION_DETAILS, SUBJECT_PAYMENT_EXTERNAL_LINKS);
      }
    },
    PAY_BY_LINK_CREATE: ({ ability, voltEmployee }) => {
      ability.can(ACTIONS_WRITE, SUBJECT_PAY_BY_LINK);
    },
    PAYMENT_SENSITIVE_DATA_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, PROPERTY_SENDER);
      ability.can(
        ACTION_DETAILS,
        SUBJECT_PAYMENT,
        PROPERTY_SENDER_ACCOUNT_NAME
      );
      ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, [
        PROPERTY_SENDER_VOLT_CONNECT,
        PROPERTY_SENDER_PAYMENT_PROVIDER,
      ]);

      if (voltEmployee) {
        ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, PROPERTY_SENDER_CHECKOUT);
      }
    },
    FIRST_PARTY_PAYMENT_CREATE: ({ ability, voltEmployee }) => {
      if (!voltEmployee) {
        ability.can(ACTIONS_WRITE, SUBJECT_CASH_MANAGEMENT, [
          PROPERTY_SANDBOX_SETTLEMENT_BATCHES,
          PROPERTY_PRODUCTION_SETTLEMENT_BATCHES,
        ]);
        ability.can(ACTION_CREATE, SUBJECT_REFUND);
        ability.can(ACTION_UPDATE, SUBJECT_REFUND);

        ability.can(ACTION_CREATE, SUBJECT_CLOSED_LOOP_PAYOUTS);
        ability.can(ACTION_UPDATE, SUBJECT_CLOSED_LOOP_PAYOUTS);
      }

      // Stablecoin: CREATE_REFUND
      ability.can(ACTION_CREATE, SUBJECT_VX2_REFUND);
    },
    THIRD_PARTY_PAYMENT_CREATE: ({ ability, voltEmployee }) => {
      if (!voltEmployee) {
        ability.can(ACTION_CREATE, SUBJECT_OPEN_LOOP_PAYOUTS);
        ability.can(ACTION_UPDATE, SUBJECT_OPEN_LOOP_PAYOUTS);
      }
    },
    PAYMENTS_APPROVE_REJECT: ({ ability, voltEmployee }) => {
      if (!voltEmployee) {
        ability.can(ACTION_APPROVE, SUBJECT_REFUND);

        ability.can(ACTION_APPROVE, SUBJECT_CLOSED_LOOP_PAYOUTS);

        ability.can(ACTION_APPROVE, SUBJECT_OPEN_LOOP_PAYOUTS);
      }
    },
    PAYMENTS_CANCEL_FAIL: ({ ability, voltEmployee }) => {},

    // customers
    CUSTOMERS_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTION_LIST, SUBJECT_CUSTOMER);
      ability.can(ACTION_DETAILS, SUBJECT_CUSTOMER, [
        PROPERTY_ID,
        PROPERTY_ADDRESS,
      ]);

      ability.can(ACTION_DETAILS, SUBJECT_VX2_ONBOARDING);

      if (!voltEmployee) {
        ability.cannot(ACTION_UPDATE, SUBJECT_CUSTOMER, [
          CUSTOMER_PROPERTY_ACTIVE,
        ]);
      }
    },
    CUSTOMERS_MANAGE: ({ ability, voltEmployee }) => {
      if (voltEmployee) {
        ability.can(ACTION_CREATE, SUBJECT_CUSTOMER, [PROPERTY_ID]);
        ability.can([ACTION_UPDATE], SUBJECT_CUSTOMER, [
          PROPERTY_ID,
          CUSTOMER_PROPERTY_ACTIVE,
          PROPERTY_MERCHANT_CATEGORY_CODE,
          PROPERTY_ADDRESS,
          PROPERTY_TYPE,
        ]);
        ability.can([ACTION_CREATE, ACTION_UPDATE], SUBJECT_CUSTOMER_COUNTRIES);
        ability.can([ACTION_CREATE, ACTION_UPDATE], SUBJECT_VX2_ONBOARDING);
        ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER, [PROPERTY_TERMINATE_AT]);

        // Stablecoin: VALIDATE_ADDRESS (admin only)
        ability.can(ACTION_CREATE, SUBJECT_VX2_ADDRESS);

        ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER_PRODUCT_FLAGS, [
          PROPERTY_SANDBOX_VERIFY,
          PROPERTY_PRODUCTION_VERIFY,
        ]);

        ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER, [
          PROPERTY_CIRCUIT_BREAKER_ENABLED,
        ]);
        ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER_PRODUCT_FLAGS, [
          PROPERTY_SANDBOX_TRANSFORMER,
          PROPERTY_PRODUCTION_TRANSFORMER,
          PROPERTY_SANDBOX_REFUND,
          PROPERTY_PRODUCTION_REFUND,
          PROPERTY_PRODUCTION_PAY_BY_LINK,
          PROPERTY_SANDBOX_PAY_BY_LINK,
          PROPERTY_PRODUCTION_PAYOUTS,
          PROPERTY_PRODUCTION_OPEN_LOOP_PAYOUTS,
          PROPERTY_SANDBOX_PAYOUTS,
          PROPERTY_SANDBOX_OPEN_LOOP_PAYOUTS,
          PROPERTY_PRODUCTION_GATEWAY,
          PROPERTY_SANDBOX_GATEWAY,
          PROPERTY_SANDBOX_CONNECT,
          PROPERTY_PRODUCTION_CONNECT,
          PROPERTY_SANDBOX_MATCHMETER,
          PROPERTY_PRODUCTION_MATCHMETER,
          PROPERTY_SANDBOX_GATEWAY_US,
          PROPERTY_SANDBOX_GATEWAY_AUSTRALIA,
          PROPERTY_PRODUCTION_GATEWAY_US,
          PROPERTY_PRODUCTION_GATEWAY_AUSTRALIA,
          PROPERTY_SANDBOX_SAME_DAY_ACH,
          PROPERTY_PRODUCTION_SAME_DAY_ACH,
          PROPERTY_PRODUCTION_SIGNAL,
          PROPERTY_SANDBOX_SIGNAL,
          PROPERTY_SANDBOX_INTERNAL_FX,
          PROPERTY_PRODUCTION_INTERNAL_FX,
          PROPERTY_PRODUCTION_ACCOUNTS,
          PROPERTY_SANDBOX_ACCOUNTS,
          PROPERTY_PRODUCTION_VX2,
          PROPERTY_SANDBOX_VX2,
          PROPERTY_PRODUCTION_NAMED_ACCOUNTS,
          PROPERTY_SANDBOX_NAMED_ACCOUNTS,
          PROPERTY_PRODUCTION_MANDATE_MANAGEMENT,
          PROPERTY_SANDBOX_MANDATE_MANAGEMENT,
        ]);
      }
    },
    API_ACCESS_MANAGE: ({ ability, voltEmployee }) => {
      ability.can(ACTION_CREATE, SUBJECT_CUSTOMER, PROPERTY_CREDENTIALS);
      ability.can([ACTION_CREATE, ACTION_UPDATE], SUBJECT_CUSTOMER_PUBLIC_KEYS);
    },
    PAYMENT_CONFIGURATION_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTIONS_READ, SUBJECT_CUSTOMER_PAYMENTS_PROVIDERS);
      ability.can(ACTIONS_READ, SUBJECT_CUSTOMER, [
        PROPERTY_PAYMENT_PREFERENCES,
      ]);
    },
    PAYMENT_CONFIGURATION_MANAGE: ({ ability }) => {
      ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER_PAYMENTS_PROVIDERS);

      ability.can([ACTION_UPDATE], SUBJECT_CUSTOMER, [
        PROPERTY_PAYMENT_PREFERENCES,
      ]);

      ability.can(ACTION_LIST, SUBJECT_CUSTOMER_BANK_LIST);
      ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER_BANK_LIST);
    },
    BANK_ACCOUNTS_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTIONS_READ, SUBJECT_ACCOUNT, PROPERTY_ID);
    },
    BANK_ACCOUNTS_MANAGE: ({ ability, voltEmployee }) => {
      if (voltEmployee) {
        ability.can(ACTIONS_READ, SUBJECT_WORKFLOWS);
        ability.can(ACTIONS_READ, SUBJECT_ACCOUNT, [PROPERTY_ID]);
        ability.can(ACTIONS_WRITE, SUBJECT_ACCOUNT, [PROPERTY_ID]);
        ability.can(ACTIONS_WRITE, SUBJECT_DYNAMIC_BENEFICIARY, [PROPERTY_ID]);
      }
    },

    // accounts
    ACCOUNTS_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTIONS_READ, SUBJECT_CASH_MANAGEMENT);
      ability.can(ACTIONS_READ, SUBJECT_CASH_MANAGEMENT_ACCOUNT);
      ability.cannot(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
        PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
        PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
        PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
        PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
      ]);
      ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
        PROPERTY_PRODUCTION_ACCOUNT_BALANCE,
        PROPERTY_SANDBOX_ACCOUNT_BALANCE,
      ]);
      ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
        PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
        PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
      ]);
      ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
        PROPERTY_PRODUCTION_SETTLEMENT_CONFIGURATION,
        PROPERTY_SANDBOX_SETTLEMENT_CONFIGURATION,
        PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
        PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
      ]);

      ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS);
      ability.can(ACTION_LIST, SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS);

      // Stablecoin: VIEW_SETTLEMENTS, VIEW_SETTLEMENT_CONFIG, VIEW_CUSTOMER_WALLETS
      ability.can(ACTIONS_READ, SUBJECT_VX2_SETTLEMENT);
      ability.can(ACTION_DETAILS, SUBJECT_VX2_SETTLEMENT_CONFIGURATION);
      ability.can(ACTIONS_READ, SUBJECT_VX2_WALLET);
    },
    ACCOUNTS_MANAGE: ({ ability, voltEmployee }) => {
      ability.can(ACTION_UPDATE, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
        PROPERTY_PRODUCTION_SETTLEMENT_CONFIGURATION,
        PROPERTY_SANDBOX_SETTLEMENT_CONFIGURATION,
        PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
        PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
      ]);

      ability.can(ACTION_UPDATE, SUBJECT_CASH_MANAGEMENT_VIRTUAL_ACCOUNTS);
      ability.can(ACTION_LIST, SUBJECT_CASH_MANAGEMENT_VIRTUAL_ACCOUNTS);

      // Stablecoin: MANAGE_SETTLEMENT_CONFIG, MANAGE_CUSTOMER_WALLETS
      ability.can(ACTION_UPDATE, SUBJECT_VX2_SETTLEMENT_CONFIGURATION);
      ability.can(ACTIONS_WRITE, SUBJECT_VX2_WALLET);

      if (voltEmployee) {
        ability.can(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_ACCOUNT);
        ability.can(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_PROVIDER_SETUP);
        ability.cannot(ACTIONS_WRITE, SUBJECT_CASH_MANAGEMENT, [
          PROPERTY_PRODUCTION_SETTLEMENT_BATCHES,
        ]);

        // Stablecoin: RUN_SETTLEMENT (admin only)
        ability.can(ACTION_CREATE, SUBJECT_VX2_SETTLEMENT);
      }
    },
    ACCOUNT_NOTIFICATION_SETTINGS_VIEW: ({ ability, voltEmployee }) => {
      ability.can(
        ACTIONS_READ,
        SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION
      );
      ability.can(ACTION_DETAILS, SUBJECT_PAYOUTS_NOTIFICATIONS);

      ability.can(ACTION_DETAILS, SUBJECT_CREDIT_NOTIFICATION_PREFERENCES);
      ability.can(ACTION_LIST, SUBJECT_CREDIT_NOTIFICATION_PREFERENCES);
    },
    ACCOUNT_NOTIFICATION_SETTINGS_MANAGE: ({ ability, voltEmployee }) => {
      ability.can(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION);

      ability.can(ACTIONS_WRITE, SUBJECT_PAYOUTS_NOTIFICATIONS, [
        PROPERTY_PAYOUT_NOTIFICATIONS_SECRET,
        PROPERTY_PAYOUT_NOTIFICATIONS_STATUS,
      ]);
      ability.can(ACTION_TEST, SUBJECT_PAYOUTS_NOTIFICATIONS);

      ability.can(ACTION_CREATE, SUBJECT_CREDIT_NOTIFICATION_PREFERENCES);
      ability.can(ACTION_TEST, SUBJECT_CREDIT_NOTIFICATION_PREFERENCES);
      ability.can(ACTION_UPDATE, SUBJECT_CREDIT_NOTIFICATION_PREFERENCES);
    },

    // developer
    APPLICATIONS_VIEW: ({ ability, voltEmployee }) => {
      ability.can([ACTION_LIST], SUBJECT_CLIENT);
      ability.can([ACTION_DETAILS], SUBJECT_CLIENT, [PROPERTY_ID]);
      ability.can([ACTION_DETAILS], SUBJECT_NOTIFICATION_SETTINGS, [
        PROPERTY_EXTRA_FIELDS,
      ]);
    },
    APPLICATIONS_MANAGE: ({ ability, voltEmployee }) => {
      ability.can(ACTION_CREATE, SUBJECT_CLIENT, [PROPERTY_ID]);
      ability.can([ACTION_UPDATE, ACTION_DELETE], SUBJECT_CLIENT, [
        PROPERTY_ID,
      ]);
      ability.can([ACTION_DETAILS], SUBJECT_CLIENT, [PROPERTY_ID]);
      ability.can(ACTION_UPDATE, SUBJECT_CLIENT, [
        CLIENT_PROPERTY_ACTIVE,
        PROPERTY_SECRET,
        PROPERTY_REDIRECT_SIGNATURE,
      ]);
      ability.can(ACTION_TEST, SUBJECT_PAYMENT_NOTIFICATION);
      ability.can(ACTIONS_CRU, SUBJECT_NOTIFICATION_SETTINGS);
      ability.cannot(ACTION_UPDATE, SUBJECT_NOTIFICATION_SETTINGS, [
        PROPERTY_EXTRA_FIELDS,
      ]);
      ability.can(ACTIONS_READ, SUBJECT_CLIENT_VERIFY_NOTIFICATIONS);
      ability.can(ACTIONS_WRITE, SUBJECT_CLIENT_VERIFY_NOTIFICATIONS);

      if (voltEmployee) {
        ability.can(
          ACTION_UPDATE,
          SUBJECT_NOTIFICATION_SETTINGS,
          PROPERTY_EXTRA_FIELDS
        );
        ability.can([ACTION_DETAILS], SUBJECT_NOTIFICATION_SETTINGS, [
          PROPERTY_EXTRA_FIELDS,
        ]);
      }
    },
    CHECKOUT_CONFIGURATION_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTIONS_READ, SUBJECT_CHECKOUT);
    },
    CHECKOUT_CONFIGURATION_MANAGE: ({ ability, voltEmployee }) => {
      ability.can([ACTION_CREATE, ACTION_UPDATE], SUBJECT_CHECKOUT);
    },

    // fraud prevention
    CIRCUIT_BREAKER_VIEW: ({ ability, voltEmployee }) => {
      ability.can([ACTION_DETAILS, ACTION_LIST], SUBJECT_CIRCUIT_BREAKER);

      if (voltEmployee) {
        ability.can(ACTION_GLOBAL_FILTER, SUBJECT_CIRCUIT_BREAKER);
      }
    },
    CIRCUIT_BREAKER_MANAGE: ({ ability, voltEmployee }) => {
      ability.can(ACTIONS_CRU, SUBJECT_CIRCUIT_BREAKER);

      if (voltEmployee) {
        ability.can(ACTION_GLOBAL_FILTER, SUBJECT_CIRCUIT_BREAKER);
      }
    },

    // users
    USERS_VIEW: ({ ability, voltEmployee }) => {
      ability.can(ACTION_LIST, SUBJECT_USER);
      ability.can(ACTION_DETAILS, SUBJECT_USER, PROPERTY_ID);
    },
    USERS_MANAGE: ({ ability, voltEmployee, userId, userType }) => {
      ability.can(ACTION_CREATE, SUBJECT_USER, PROPERTY_ID);
      ability.can(ACTION_UPDATE, SUBJECT_USER, [
        PROPERTY_ID,
        USER_PROPERTY_ACTIVE,
      ]);
      ability.cannot(ACTION_UPDATE, SUBJECT_USER, USER_PROPERTY_ACTIVE, {
        id: userId,
      });
      ability.can(ACTIONS_READ, SUBJECT_USER, PROPERTY_ID);

      if (userType === USER_TYPE.OWNER) {
        ability.can(
          [ACTION_CREATE, ACTION_DEMOTE],
          SUBJECT_OWNER_USER,
          PROPERTY_ID
        );
        ability.can([ACTION_PROMOTE], SUBJECT_USER, PROPERTY_ID);
        ability.can(ACTIONS_READ, SUBJECT_OWNER_USER, PROPERTY_ID);
        ability.can(ACTION_UPDATE, SUBJECT_OWNER_USER, [
          PROPERTY_ID,
          USER_PROPERTY_ACTIVE,
        ]);
        ability.cannot(
          [ACTION_UPDATE, ACTION_PROMOTE, ACTION_DEMOTE],
          SUBJECT_OWNER_USER,
          USER_PROPERTY_ACTIVE,
          {
            id: userId,
          }
        );
      }

      if (voltEmployee) {
        ability.can(ACTION_PROMOTE, SUBJECT_USER, PROPERTY_ID);
      }
    },
    ADMINS_MANAGE: ({ ability, voltEmployee, userId }) => {
      if (voltEmployee) {
        ability.can(ACTION_CREATE, SUBJECT_ADMIN, PROPERTY_ID);
        ability.can(ACTIONS_READ, SUBJECT_ADMIN, PROPERTY_ID);
        ability.can(ACTION_UPDATE, SUBJECT_ADMIN, [
          PROPERTY_ID,
          USER_PROPERTY_ACTIVE,
        ]);
        ability.cannot(ACTION_UPDATE, SUBJECT_ADMIN, USER_PROPERTY_ACTIVE, {
          id: userId,
        });
      }
    },

    // administration
    BANKS_MANAGE: ({ ability, voltEmployee }) => {},
    FEATURE_FLAGS_MANAGE: ({ ability, voltEmployee }) => {},
    WORKFLOWS_APPROVE: ({ ability, voltEmployee }) => {
      ability.can(ACTION_APPROVE, SUBJECT_WORKFLOWS);
    },
    BILLING_ENGINE_MANAGE: ({ ability, voltEmployee }) => {},
    REPORTER_VIEW: ({ ability, voltEmployee }) => {},
    REPORTER_MANAGE: ({ ability, voltEmployee }) => {},
    CM_EXCEPTIONS_VIEW: ({ ability, voltEmployee }) => {},
    CM_EXCEPTIONS_MANAGE: ({ ability, voltEmployee }) => {},
    COMPLIANCE_REPORTING_MANAGE: ({ ability, voltEmployee }) => {},
    VOLT_ACCOUNTS_MANAGE: ({ ability, voltEmployee }) => {
      if (voltEmployee) {
        ability.can(ACTION_CREATE, SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS);
        ability.can(ACTION_UPDATE, SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS);
      }
    },
    FINANCIAL_OPERATIONS_MANAGE: ({ ability, voltEmployee }) => {},
  };
